import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b09cb0cc"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 2,
  class: "container"
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", null, "AI Agent", -1))
const _hoisted_3 = { class: "row data center horizontal-center" }
const _hoisted_4 = { class: "text-center" }
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "AI Agent Interactions", -1))
const _hoisted_6 = { class: "text-center" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Rentals Created by AI Agent", -1))

import { ref, computed } from "vue";
import api from "@virgodev/bazaar/functions/api";
import BaseSpinner from "@/components/BaseSpinner.vue";
import BaseAlert from "@/components/BaseAlert.vue";
import Filter from "@/components/Filter.vue";
import { useStore } from "@/store/index";
import BaseLineChart from "@/components/BaseLineChart.vue";
import { parseYMDLocal, monthLabel } from "@/utils/timeSeries";
import "@vueform/multiselect/themes/default.scss";

interface Facility {
  facility_name: string;
}

interface Client {
  name: string;
  facilities: Facility[];
}

interface CreateRentalsReportRow {
  start_date: string;
  create_rentals: number | string;
}

interface CallsMadeReportRow {
  start_date: string;
  calls_made: number | string;
}


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const store = useStore();
const formSubmitting = ref(false);
const errors = ref("");
const status = ref("loading");
const isLoading = computed(
  () => status.value === "loading" || formSubmitting.value
);

const client = ref<Client | undefined>();
const facilityOptions = ref(
  Array<{ label: string; value: string | null; disabled: boolean }>()
);

const dateOptions = ref([
  { text: "Current month", value: "current_month" },
  { text: "Last month", value: "last_month" },
  { text: "All year", value: "all_year" },
  { text: "Custom Dates", value: "custom" },
]);

const formFields = computed(() => store.clientPortalFormFields);

const facilityData = ref<{
  ai_agent_calls_count: number | null;
  ai_agent_calls_report: CallsMadeReportRow[];
  ai_agent_create_rentals: number;
  ai_agent_create_rentals_report: CreateRentalsReportRow[];
}>({
  ai_agent_calls_count: null,
  ai_agent_calls_report: [],
  ai_agent_create_rentals: 0,
  ai_agent_create_rentals_report: [],
});

const asInt = (n: number | string) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

const selectedDate = computed(
  () => (store.clientPortalFormFields || {}).selected_date
);

function buildReportSeries(
  rows: { start_date: string; value: number | string }[]
) {
  const sorted = rows.slice().sort((a, b) => {
    const da = parseYMDLocal(a.start_date)?.getTime() ?? 0;
    const db = parseYMDLocal(b.start_date)?.getTime() ?? 0;
    return da - db;
  });

  const needsTrend =
    selectedDate.value === "current_month" ||
    selectedDate.value === "last_month";
  const windowed = needsTrend ? sorted.slice(-4) : sorted;

  return {
    labels: windowed.map((r) => {
      const d = parseYMDLocal(r.start_date);
      return d ? monthLabel(d) : "Unknown";
    }),
    data: windowed.map((r) => Number(r.value ?? 0)),
  };
}

function buildCreateRentalsSeries() {
  return buildReportSeries(
    (facilityData.value.ai_agent_create_rentals_report || []).map((r) => ({
      start_date: r.start_date,
      value: r.create_rentals,
    }))
  );
}

function buildCallsSeries() {
  return buildReportSeries(
    (facilityData.value.ai_agent_calls_report || []).map((r) => ({
      start_date: r.start_date,
      value: r.calls_made,
    }))
  );
}

const series = computed(() => ({
  calls: buildCallsSeries(),
  createRentals: buildCreateRentalsSeries(),
}));

function toYMD(d: string | Date | null | undefined) {
  return d instanceof Date ? d.toISOString().slice(0, 10) : d;
}

async function getUserInfo() {
  status.value = "loading";
  errors.value = "";
  const response = await api({ url: `client/` });
  if (response.status == 200) {
    client.value = response.body[0] as Client;
    facilityOptions.value = [
      { label: "All Facilities", value: null, disabled: true },
      ...client.value.facilities.map((f: Facility) => ({
        label: f.facility_name,
        value: f.facility_name,
        disabled: false,
      })),
    ];
    await submitForm();
  } else {
    errors.value =
      "Unable to communicate with the server. Please check your connection and try again.";
  }
  status.value = "loaded";
}

async function submitForm(formData?: typeof formFields.value) {
  formSubmitting.value = true;
  errors.value = "";
  if (formData) store.setClientPortalFormFields(formData);
  const raw = formData ?? store.clientPortalFormFields;

  const payload = {
    ...raw,
    start_date: toYMD(raw.start_date),
    end_date: toYMD(raw.end_date),
  };

  if (!payload.selected_facilities?.length) {
    payload.selected_facilities =
      client.value?.facilities.map(
        (f: { facility_name: string }) => f.facility_name
      ) || [];
  }

  const res = await api({
    url: `client/facility_data/`,
    method: "POST",
    json: payload,
  });

  if (res.status === 200) {
    const body = res.body || {};
    const createRentalsReport = Array.isArray(
      body.ai_agent_create_rentals_report
    )
      ? body.ai_agent_create_rentals_report
      : [];
    const callsReport = Array.isArray(body.ai_agent_calls_report)
      ? body.ai_agent_calls_report
      : [];

    facilityData.value = {
      ai_agent_calls_count:
        body.ai_agent_calls_count === undefined ||
        body.ai_agent_calls_count === null
          ? null
          : Number(body.ai_agent_calls_count),
      ai_agent_calls_report: callsReport.map((r: Record<string, unknown>) => ({
        start_date: String(r.start_date ?? ""),
        calls_made: Number(r.calls_made ?? 0),
      })),
      ai_agent_create_rentals: Number(body.ai_agent_create_rentals ?? 0),
      ai_agent_create_rentals_report: createRentalsReport.map(
        (r: Record<string, unknown>) => ({
          start_date: String(r.start_date ?? ""),
          create_rentals: Number(r.create_rentals ?? 0),
        })
      ),
    };
  } else {
    errors.value = "Unable to communicate with the server. Please try again.";
  }

  formSubmitting.value = false;
}

function handleFilterSubmit(newFields: typeof formFields.value) {
  store.setClientPortalFormFields(newFields);
  submitForm(newFields);
}

getUserInfo();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (errors.value)
      ? (_openBlock(), _createBlock(BaseAlert, {
          key: 0,
          variant: "danger",
          class: "js-form-error"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(errors.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(isLoading))
      ? (_openBlock(), _createBlock(BaseSpinner, {
          key: 1,
          preset: "large"
        }))
      : _createCommentVNode("", true),
    (client.value && !_unref(isLoading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("h1", null, _toDisplayString(client.value.name), 1),
          _createVNode(Filter, {
            "facility-options": facilityOptions.value,
            "date-options": dateOptions.value,
            "initial-form-fields": _unref(formFields),
            "form-submitting": formSubmitting.value,
            onSubmit: handleFilterSubmit
          }, null, 8, ["facility-options", "date-options", "initial-form-fields", "form-submitting"]),
          _hoisted_2,
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _hoisted_5,
              _createElementVNode("h3", null, _toDisplayString(asInt(facilityData.value.ai_agent_calls_count ?? 0)), 1),
              _createVNode(BaseLineChart, {
                labels: _unref(series).calls.labels,
                datasets: [
            { label: 'AI Agent Interactions', data: _unref(series).calls.data },
          ],
                "y-tick-format": asInt
              }, null, 8, ["labels", "datasets"])
            ]),
            _createElementVNode("div", _hoisted_6, [
              _hoisted_7,
              _createElementVNode("h3", null, _toDisplayString(asInt(facilityData.value.ai_agent_create_rentals)), 1),
              _createVNode(BaseLineChart, {
                labels: _unref(series).createRentals.labels,
                datasets: [
            {
              label: 'AI Agent Create Rentals',
              data: _unref(series).createRentals.data,
            },
          ],
                "y-tick-format": asInt
              }, null, 8, ["labels", "datasets"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})